import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { filter, map, startWith } from 'rxjs/operators';
import { Select } from 'primeng/select';
import { ThemeService } from '../../../core/services/theme.service';
import { AuthService } from '../../../core/services/auth.service';
import { CompanyService } from '../../../core/services/company.service';
import { CampaignService } from '../../../core/services/campaign.service';

interface RouteSlugs {
	companySlug: string | null;
	campaignSlug: string | null;
}

@Component({
	selector: 'app-nav',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [RouterLink, RouterLinkActive, FormsModule, Select],
	templateUrl: './nav.html',
	styleUrl: './nav.scss',
})
export class NavComponent {
	protected themeService = inject(ThemeService);
	protected auth = inject(AuthService);
	protected companyService = inject(CompanyService);
	protected campaignService = inject(CampaignService);
	private router = inject(Router);

	private readonly routeSlugs = toSignal(
		this.router.events.pipe(
			filter((e): e is NavigationEnd => e instanceof NavigationEnd),
			map(() => this.extractSlugs(this.router.routerState.snapshot.root)),
			startWith(this.extractSlugs(this.router.routerState.snapshot.root)),
		),
		{ initialValue: { companySlug: null, campaignSlug: null } as RouteSlugs },
	);

	protected readonly activeCampaignSlug = computed(() => this.routeSlugs().campaignSlug);

	protected readonly activeCampaign = computed(() => {
		const slug = this.activeCampaignSlug();
		if (!slug) return null;
		return this.campaignService.campaigns().find(c => c.slug === slug) ?? null;
	});

	private lastLoadedCompanySlug: string | null = null;

	constructor() {
		effect(() => {
			const { companySlug, campaignSlug } = this.routeSlugs();
			if (!companySlug || !campaignSlug) return;
			if (this.lastLoadedCompanySlug === companySlug) return;
			if (this.campaignService.campaigns().some(c => c.slug === campaignSlug)) {
				this.lastLoadedCompanySlug = companySlug;
				return;
			}
			this.lastLoadedCompanySlug = companySlug;
			this.campaignService.loadCampaigns(companySlug);
		});
	}

	protected switchCompany(slug: string): void {
		if (!slug) return;
		const current = this.companyService.activeCompany();
		if (current?.slug === slug) return;
		this.companyService.setActiveBySlug(slug);
		this.router.navigateByUrl(`/${slug}/campaigns`);
	}

	private extractSlugs(route: ActivatedRouteSnapshot): RouteSlugs {
		let companySlug: string | null = (route.params['companySlug'] as string | undefined) ?? null;
		let campaignSlug: string | null = (route.params['campaignSlug'] as string | undefined) ?? null;
		for (const child of route.children) {
			const childSlugs = this.extractSlugs(child);
			if (childSlugs.companySlug) companySlug = childSlugs.companySlug;
			if (childSlugs.campaignSlug) campaignSlug = childSlugs.campaignSlug;
		}
		return { companySlug, campaignSlug };
	}
}
